/**
 * Procedural skirmish terrain — terraces, cliffs, ramps, rivers, biomes.
 * Pure functions so the sim and the client share one map from the seed.
 */

export type TerrainStyle = "highlands" | "woodlands" | "dry_arabia";

export type TerrainTree = { x: number; z: number; s: number; hue: number };
export type TerrainSite = { x: number; z: number; kind: "sacred" | "trade" };

let viewSeed = 11;
let viewBiome = "";

export function terrainStyle(biome: string | null | undefined): TerrainStyle {
  if (biome === "forest") return "woodlands";
  if (biome === "plains" || biome === "coastal") return "dry_arabia";
  return "highlands";
}

/** Client view. The sim always passes seed explicitly. */
export function bindTerrainView(seed: number, biome?: string | null) {
  viewSeed = seed || 11;
  viewBiome = biome || "";
}

function hash2(ix: number, iz: number, seed: number): number {
  let n =
    Math.imul(ix | 0, 374761393) +
    Math.imul(iz | 0, 668265263) +
    Math.imul(seed | 0, 1440662683);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

function vnoise(x: number, z: number, seed: number): number {
  const x0 = Math.floor(x);
  const z0 = Math.floor(z);
  const fx = x - x0;
  const fz = z - z0;
  const sx = fx * fx * (3 - 2 * fx);
  const sz = fz * fz * (3 - 2 * fz);
  const a = hash2(x0, z0, seed);
  const b = hash2(x0 + 1, z0, seed);
  const c = hash2(x0, z0 + 1, seed);
  const d = hash2(x0 + 1, z0 + 1, seed);
  return a + (b - a) * sx + (c - a) * sz + (a - b - c + d) * sx * sz;
}

function fbm(x: number, z: number, seed: number): number {
  const a = vnoise(x, z, seed);
  const b = vnoise(x * 2.05, z * 2.05, seed + 97);
  return a * 0.68 + b * 0.32;
}

/** River centerline. x is the parameter; z wobbles. Fords are shallow gaps. */
export function riverAxis(
  seed: number,
  x: number
): { z: number; ford: boolean; t: number } {
  const t = x / 78;
  const phase = (seed % 80) * 0.05;
  const z =
    Math.sin(t * 3.05 + phase) * 6.5 +
    Math.sin(t * 7.4 + phase * 1.7) * 2.1;
  const ford =
    Math.abs(t + 0.4) < 0.06 ||
    Math.abs(t - 0.08) < 0.055 ||
    Math.abs(t - 0.5) < 0.06;
  return { z, ford, t };
}

/** Flat water surface. Land and buildings stay above this. The lake bed sits below it. */
export const WATERLINE = 0.58;

/** Playable land, edge to edge. About three times the old skirmish. */
export const MAP_HALF = 96;

/** Town halls sit this far in from the map edge, on the corners. */
export const TOWN_INSET = 22;

/** Eight start pads: four corners, then the middle of each edge. */
export const TOWN_PADS: ReadonlyArray<{ x: number; z: number }> = (() => {
  const c = MAP_HALF - TOWN_INSET;
  return [
    { x: -c, z: -c },
    { x: c, z: -c },
    { x: c, z: c },
    { x: -c, z: c },
    { x: 0, z: -c },
    { x: c, z: 0 },
    { x: 0, z: c },
    { x: -c, z: 0 },
  ];
})();

/** Distance from the center to a corner town. */
export const SPAWN_RADIUS = Math.hypot(MAP_HALF - TOWN_INSET, MAP_HALF - TOWN_INSET);

/** Gap between town pads, so the outlet misses every spawn. */
export function outletGap(seed: number): number {
  return -Math.PI / 8 + (Math.abs(Math.floor(seed)) % 8) * (Math.PI / 4);
}

/** River from the lake to the map edge, then a strip of ocean. */
export function channelFrame(
  x: number,
  z: number,
  seed: number
): { along: number; across: number; lakeAlong: number } {
  const gap = outletGap(seed);
  const dx = Math.cos(gap);
  const dz = Math.sin(gap);
  const L = lakeShape(seed);
  return {
    along: x * dx + z * dz,
    across: -x * dz + z * dx,
    lakeAlong: L.x * dx + L.z * dz,
  };
}

export function inChannel(x: number, z: number, seed: number): boolean {
  const c = channelFrame(x, z, seed);
  if (c.along < c.lakeAlong - 1.5) return false;
  if (c.along > MAP_HALF + 2) return false;
  const wide = c.along > 48 ? 14 : 4.6;
  return Math.abs(c.across) < wide;
}

/** One cruise lake near the middle, clear of the town ring. */
export function lakeShape(seed: number): { x: number; z: number; rx: number; rz: number } {
  const ang = hash2(9, 3, seed) * Math.PI * 2;
  return {
    x: Math.cos(ang) * 4,
    z: Math.sin(ang) * 4,
    rx: 20,
    rz: 14.5,
  };
}

function lakeUnit(x: number, z: number, seed: number): number {
  const L = lakeShape(seed);
  return Math.hypot((x - L.x) / L.rx, (z - L.z) / L.rz);
}

const SPAWN_RING: Array<[number, number]> = TOWN_PADS.map((p) => [p.x, p.z]);

type RampSeg = { x0: number; z0: number; dx: number; dz: number; len2: number };

function rampSegs(_seed: number): RampSeg[] {
  const out: RampSeg[] = [];
  for (let i = 0; i < 4; i++) {
    const pad = TOWN_PADS[i];
    const x0 = pad.x * 0.2;
    const z0 = pad.z * 0.2;
    const dx = pad.x - x0;
    const dz = pad.z - z0;
    out.push({ x0, z0, dx, dz, len2: dx * dx + dz * dz || 1 });
  }
  return out;
}

function rampFactor(segs: RampSeg[], x: number, z: number): number {
  let best = 0;
  for (const r of segs) {
    let u = ((x - r.x0) * r.dx + (z - r.z0) * r.dz) / r.len2;
    if (u < 0) u = 0;
    else if (u > 1) u = 1;
    const px = r.x0 + r.dx * u;
    const pz = r.z0 + r.dz * u;
    const d = Math.hypot(x - px, z - pz);
    const f = d >= 1.7 ? 0 : 1 - d / 1.7;
    if (f > best) best = f;
  }
  return best;
}

function spawnFlat(x: number, z: number): number {
  let gate = 1;
  for (let i = 0; i < 8; i++) {
    const sx = SPAWN_RING[i][0];
    const sz = SPAWN_RING[i][1];
    const d = Math.hypot(x - sx, z - sz);
    const t = d <= 3.5 ? 0 : d >= 9.5 ? 1 : (d - 3.5) / 6;
    if (t < gate) gate = t;
  }
  return gate;
}

function resolve(seed?: number, biome?: string | null): { seed: number; style: TerrainStyle } {
  const s = seed == null || !Number.isFinite(seed) ? viewSeed : seed;
  const style =
    biome != null && biome !== ""
      ? terrainStyle(biome)
      : s === viewSeed
        ? terrainStyle(viewBiome)
        : "highlands";
  return { seed: s, style };
}

const FIELD_STEP = 2;
const FIELD_N = MAP_HALF + 1;
const FIELD_ORG = MAP_HALF;

type HeightField = { h: Float32Array; cliff: Uint8Array };

const fieldCache = new Map<string, HeightField>();

function rawHeight(
  x: number,
  z: number,
  seed: number,
  style: TerrainStyle,
  ramps: RampSeg[],
  biome: string
): number {
  const amp = style === "dry_arabia" ? 2.15 : style === "woodlands" ? 2.7 : 3.7;
  const n = fbm(x * 0.075, z * 0.075, seed);
  const n2 = vnoise(x * 0.16 + 12, z * 0.16 - 4, seed + 19);
  let h = n * 0.74 + n2 * 0.26;
  const steps = style === "dry_arabia" ? 3 : 4;
  const snapped = Math.floor(Math.max(0, h) * steps) / steps;
  const ramp = rampFactor(ramps, x, z);
  h = snapped * (1 - ramp) + Math.max(0, h) * ramp;
  let height = h * amp;

  const mid = Math.hypot(x, z);
  if (mid < 12 && ramp < 0.45) {
    const shelf = style === "dry_arabia" ? 1.15 : 2.35;
    const u = Math.max(0, Math.min(1, (12 - mid) / 5));
    height = Math.max(height, shelf * u * u * (3 - 2 * u));
  }

  const gate = spawnFlat(x, z);
  if (gate < 1) {
    const terrace = WATERLINE + 0.95;
    height = terrace * (1 - gate) + height * gate;
  }
  if (Math.abs(x) > MAP_HALF + 2 || Math.abs(z) > MAP_HALF + 2) height *= 0.45;

  const sea =
    biome === "islands" ||
    biome === "archipelago" ||
    biome === "twin_seas" ||
    biome === "coastal";
  if (!sea) {
    const u = lakeUnit(x, z, seed);
    if (u < 1.08) {
      height = 0.06 + 0.18 * u * u;
    } else if (u < 1.48) {
      const t = (u - 1.08) / 0.4;
      const lip = Math.max(height, WATERLINE + 0.85);
      height = (WATERLINE - 0.2) * (1 - t) + lip * t;
    }
  }

  // Walkable mountains. The slope stays under the cliff cutoff so units can climb
  // them, and town pads plus the lake are left alone.
  if (gate > 0.55 && (sea || lakeUnit(x, z, seed) > 1.55)) {
    for (let i = 0; i < 3; i++) {
      const ang = hash2(20 + i, 4, seed) * Math.PI * 2;
      const dist = 32 + hash2(21 + i, 5, seed) * 18;
      const px = Math.cos(ang) * dist;
      const pz = Math.sin(ang) * dist;
      const radius = 8 + hash2(22 + i, 6, seed) * 2.4;
      const peak = 1.45 + hash2(23 + i, 7, seed) * 0.85;
      const d = Math.hypot(x - px, z - pz);
      if (d >= radius) continue;
      const t = 1 - d / radius;
      const dome = peak * t * t * (3 - 2 * t);
      height = Math.max(height, 0.55 + dome);
    }
  }

  // River out of the lake into the ocean, kept off the town pads.
  if (!sea && spawnFlat(x, z) > 0.42) {
    const c = channelFrame(x, z, seed);
    if (c.along > c.lakeAlong - 1.2 && c.along < MAP_HALF + 2) {
      const half = c.along > 48 ? 12 : 4;
      const ad = Math.abs(c.across);
      if (ad < half) {
        const t = ad / half;
        const bed = c.along > 22 ? 0.04 : 0.08;
        height = Math.min(height, bed + (WATERLINE - 0.12) * t * t);
      } else if (ad < half + 1.8) {
        const t = (ad - half) / 1.8;
        const lip = WATERLINE + 0.08;
        height = Math.min(height, lip * (1 - t) + height * t);
      }
    }
  }
  return height > 0.02 ? height : 0.02;
}

function fieldFor(seed?: number, biome?: string | null): HeightField {
  const { seed: s, style } = resolve(seed, biome);
  const b = biome != null && biome !== "" ? biome : s === viewSeed ? viewBiome : "highlands";
  const key = `${s}|${style}|${b}`;
  const hit = fieldCache.get(key);
  if (hit) return hit;
  const h = new Float32Array(FIELD_N * FIELD_N);
  const ramps = rampSegs(s);
  for (let iz = 0; iz < FIELD_N; iz++) {
    const z = iz * FIELD_STEP - FIELD_ORG;
    for (let ix = 0; ix < FIELD_N; ix++) {
      h[iz * FIELD_N + ix] = rawHeight(ix * FIELD_STEP - FIELD_ORG, z, s, style, ramps, b);
    }
  }
  const cliff = new Uint8Array(FIELD_N * FIELD_N);
  for (let iz = 1; iz < FIELD_N - 1; iz++) {
    for (let ix = 1; ix < FIELD_N - 1; ix++) {
      const i = iz * FIELD_N + ix;
      const c = h[i];
      const slope = Math.max(
        Math.abs(h[i - 1] - c),
        Math.abs(h[i + 1] - c),
        Math.abs(h[i - FIELD_N] - c),
        Math.abs(h[i + FIELD_N] - c)
      );
      cliff[i] = slope > 1.05 ? 1 : 0;
    }
  }
  const field = { h, cliff };
  fieldCache.set(key, field);
  if (fieldCache.size > 4) {
    const oldest = fieldCache.keys().next().value;
    if (oldest) fieldCache.delete(oldest);
  }
  return field;
}

export function terrainHeight(
  x: number,
  z: number,
  seed?: number,
  biome?: string | null
): number {
  // The height mesh is for the picture. Building it on the server blows the
  // CPU limit and every click comes back "server is busy".
  if (typeof window === "undefined") return 1;
  const field = fieldFor(seed, biome);
  const gx = (x + FIELD_ORG) / FIELD_STEP;
  const gz = (z + FIELD_ORG) / FIELD_STEP;
  const x0 = Math.max(0, Math.min(FIELD_N - 2, Math.floor(gx)));
  const z0 = Math.max(0, Math.min(FIELD_N - 2, Math.floor(gz)));
  const fx = gx - x0;
  const fz = gz - z0;
  const i = z0 * FIELD_N + x0;
  const a = field.h[i];
  const b = field.h[i + 1];
  const c = field.h[i + FIELD_N];
  const d = field.h[i + FIELD_N + 1];
  const jump = Math.max(Math.abs(a - b), Math.abs(a - c), Math.abs(b - d), Math.abs(c - d));
  if (jump > 1.05) {
    const ix = fx < 0.5 ? x0 : x0 + 1;
    const iz = fz < 0.5 ? z0 : z0 + 1;
    return field.h[iz * FIELD_N + ix];
  }
  return a + (b - a) * fx + (c - a) * fz + (a - b - c + d) * fx * fz;
}

/** Shallow river crossing. Walkable even when the bank samples as a cliff. */
export function isFord(
  x: number,
  z: number,
  seed?: number,
  biome?: string | null
): boolean {
  const { seed: s } = resolve(seed, biome);
  if (Math.abs(x) > MAP_HALF - 8) return false;
  const axis = riverAxis(s, x);
  return axis.ford && Math.abs(z - axis.z) < 4.4;
}

export function isCliffFace(
  x: number,
  z: number,
  seed?: number,
  biome?: string | null
): boolean {
  if (typeof window === "undefined") return false;
  if (isFord(x, z, seed, biome)) return false;
  const field = fieldFor(seed, biome);
  const ix = Math.max(0, Math.min(FIELD_N - 1, Math.round((x + FIELD_ORG) / FIELD_STEP)));
  const iz = Math.max(0, Math.min(FIELD_N - 1, Math.round((z + FIELD_ORG) / FIELD_STEP)));
  return field.cliff[iz * FIELD_N + ix] === 1;
}

export function inStealthThicket(
  x: number,
  z: number,
  seed?: number,
  biome?: string | null
): boolean {
  const { seed: s, style } = resolve(seed, biome);
  if (style === "dry_arabia") return false;
  const ang = hash2(4, 8, s) * Math.PI * 2;
  const cx = Math.cos(ang) * 22;
  const cz = Math.sin(ang) * 16 + 8;
  return Math.hypot(x - cx, z - cz) < (style === "woodlands" ? 8 : 6.4);
}

export function terrainSites(seed: number, biome?: string | null): TerrainSite[] {
  const { seed: s } = resolve(seed, biome);
  const ang = hash2(2, 9, s) * Math.PI * 2;
  return [
    { x: Math.cos(ang) * 2.5, z: Math.sin(ang) * 2.5, kind: "sacred" },
    { x: -58, z: 36, kind: "trade" },
    { x: 58, z: -36, kind: "trade" },
  ];
}

/** Visual trees — winding lines and one thicket, not a tree on every tile. */
export function terrainTrees(seed: number, biome?: string | null): TerrainTree[] {
  const { seed: s, style } = resolve(seed, biome);
  const out: TerrainTree[] = [];
  const lines = style === "woodlands" ? 4 : style === "dry_arabia" ? 2 : 3;
  const per = style === "woodlands" ? 54 : style === "dry_arabia" ? 22 : 40;
  for (let line = 0; line < lines; line++) {
    const side = line % 2 === 0 ? 1 : -1;
    for (let i = 0; i < per; i++) {
      const t = -0.9 + (i / per) * 1.8;
      const x = t * 70 + (hash2(i, line, s) - 0.5) * 2.4;
      const axis = riverAxis(s, x);
      const z =
        axis.z + side * (12 + line * 6) + (hash2(line, i, s + 3) - 0.5) * 3;
      if (Math.abs(x) > MAP_HALF - 4 || Math.abs(z) > MAP_HALF - 4) continue;
      if (spawnFlat(x, z) < 0.55) continue;
      if (isCliffFace(x, z, s, biome)) continue;
      const rd = Math.abs(z - axis.z);
      if (rd < 2.2) continue;
      out.push({
        x,
        z,
        s: 0.75 + hash2(i + 5, line, s) * 0.7,
        hue: hash2(i, line + 2, s),
      });
    }
  }
  const thicket = style === "dry_arabia" ? 18 : 70;
  const ang = hash2(4, 8, s) * Math.PI * 2;
  const cx = Math.cos(ang) * 22;
  const cz = Math.sin(ang) * 16 + 8;
  for (let i = 0; i < thicket; i++) {
    const a = hash2(i, 20, s) * Math.PI * 2;
    const r = hash2(i, 21, s) * 4.2;
    const x = cx + Math.cos(a) * r;
    const z = cz + Math.sin(a) * r;
    if (spawnFlat(x, z) < 0.4) continue;
    if (terrainHeight(x, z, s, biome) < WATERLINE + 0.2) continue;
    out.push({ x, z, s: 0.9 + hash2(i, 22, s) * 0.55, hue: 0.2 + hash2(i, 23, s) * 0.3 });
  }
  const woods = style === "dry_arabia" ? 80 : 420;
  for (let i = 0; i < woods; i++) {
    const x = (hash2(i, 60, s) - 0.5) * (MAP_HALF * 1.7);
    const z = (hash2(i, 61, s) - 0.5) * (MAP_HALF * 1.7);
    if (Math.abs(x) > MAP_HALF - 6 || Math.abs(z) > MAP_HALF - 6) continue;
    if (spawnFlat(x, z) < 0.72) continue;
    if (inChannel(x, z, s)) continue;
    if (terrainHeight(x, z, s, biome) < WATERLINE + 0.22) continue;
    out.push({
      x,
      z,
      s: 0.85 + hash2(i, 62, s) * 0.85,
      hue: hash2(i, 63, s),
    });
  }
  const edgeCount = style === "dry_arabia" ? 48 : 180;
  for (let i = 0; i < edgeCount; i++) {
    const edge = i % 4;
    const t = hash2(i, 40, s);
    const jitter = (hash2(i, 41, s) - 0.5) * 10;
    const span = MAP_HALF * 1.6;
    const band = MAP_HALF - 22;
    let x = 0;
    let z = 0;
    if (edge === 0) {
      x = -span / 2 + t * span;
      z = band + jitter;
    } else if (edge === 1) {
      x = -span / 2 + t * span;
      z = -band + jitter;
    } else if (edge === 2) {
      x = band + jitter;
      z = -span / 2 + t * span;
    } else {
      x = -band + jitter;
      z = -span / 2 + t * span;
    }
    if (Math.abs(x) > MAP_HALF - 4 || Math.abs(z) > MAP_HALF - 4) continue;
    if (spawnFlat(x, z) < 0.62) continue;
    if (terrainHeight(x, z, s, biome) < WATERLINE + 0.25) continue;
    if (inChannel(x, z, s)) continue;
    out.push({ x, z, s: 1.05 + hash2(i, 42, s) * 0.7, hue: hash2(i, 43, s) });
  }
  return out;
}

export function riverWaterSamples(seed: number): Array<{ x: number; z: number }> {
  const out: Array<{ x: number; z: number }> = [];
  for (let x = -(MAP_HALF - 8); x <= MAP_HALF - 8; x += 1.4) {
    const axis = riverAxis(seed, x);
    if (axis.ford) continue;
    if (Math.abs(axis.t) > 1.05) continue;
    out.push({ x, z: axis.z });
  }
  return out;
}

export function palette(style: TerrainStyle, cliff: boolean, high: number, sand: number) {
  if (style === "dry_arabia") {
    if (cliff) return [0.55, 0.4, 0.28];
    const r = 0.72 - high * 0.08;
    const g = 0.58 - high * 0.06 + sand * 0.04;
    const b = 0.32;
    return [r, g, b];
  }
  if (cliff) return style === "woodlands" ? [0.34, 0.32, 0.28] : [0.42, 0.4, 0.36];
  if (style === "woodlands") {
    return [0.16 + high * 0.08, 0.38 + high * 0.12, 0.14];
  }
  return [0.22 + high * 0.1, 0.42 + high * 0.16, 0.16 + high * 0.04];
}
